import React, { useEffect, useState } from 'react';
import { Building2, Calendar, Trophy, Target, Star, Zap, Loader2, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { blackout } from '../services/api';
import BlackoutCountdown from '../components/BlackoutCountdown';
import BlackoutSubmitBusinessModal from '../components/BlackoutSubmitBusinessModal';
import BlackoutSubmitEventModal from '../components/BlackoutSubmitEventModal';
import BlackoutIntroVideo from '../components/BlackoutIntroVideo';

/** Logged-in member Blackout Game dashboard. */
export default function BlackoutGame() {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [bizOpen, setBizOpen] = useState(false);
  const [evOpen, setEvOpen] = useState(false);

  const load = async () => {
    const [s, m] = await Promise.all([
      blackout.myStats().catch(() => null),
      blackout.mySubmissions(20).catch(() => []),
    ]);
    setStats(s); setSubs(m);
  };
  useEffect(() => { load(); }, []);

  const onAnySuccess = () => load();

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="blackout-dashboard">
      <BlackoutIntroVideo />
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/30 via-black to-neutral-900 p-6 md:p-8">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-emerald-500/20 blur-3xl rounded-full pointer-events-none"/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-black uppercase tracking-wider">
            <Zap size={12}/> The Blackout Game
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-black">Black out the map. Fill the calendar.</h1>
          <p className="mt-2 text-neutral-300 max-w-2xl">
            Find Black-owned businesses and events we're missing. Earn <b className="text-emerald-300">25 points</b> for every new discovery, <b className="text-amber-300">10 points</b> for a duplicate confirm, and <b className="text-fuchsia-300">+100 bonus</b> when your find upgrades to a paid listing.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => setBizOpen(true)} data-testid="blackout-add-business"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm uppercase tracking-wider shadow-lg shadow-emerald-500/30 transition">
              <Building2 size={16}/> Add a Business
            </button>
            <button onClick={() => setEvOpen(true)} data-testid="blackout-add-event"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-black text-sm uppercase tracking-wider shadow-lg shadow-fuchsia-500/30 transition">
              <Calendar size={16}/> Add an Event
            </button>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Businesses Discovered" value={stats?.businessesDiscovered ?? '—'} tint="emerald" testId="stat-businesses"/>
        <StatCard icon={Calendar} label="Events Discovered" value={stats?.eventsDiscovered ?? '—'} tint="fuchsia" testId="stat-events"/>
        <StatCard icon={Star} label="Points Earned" value={stats?.totalPointsFromGame ?? '—'} suffix="pts" tint="amber" testId="stat-points"/>
        <StatCard icon={Trophy} label="Leaderboard Rank" value={stats?.leaderboardRank ? `#${stats.leaderboardRank}` : '—'} tint="sky" testId="stat-rank"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BlackoutCountdown amountUsd={stats?.cashEarningsLocked || 0}/>
        <div className="pbb-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Target size={16} className="text-emerald-400"/>
            <h3 className="font-bold text-sm uppercase tracking-wider">Current Balance</h3>
          </div>
          <div className="text-4xl font-black text-emerald-300 tabular-nums" data-testid="blackout-point-balance">
            {stats?.currentPointsBalance ?? '—'}
          </div>
          <div className="text-xs text-neutral-400 mt-1">Points redeemable now via Loyalty rewards.</div>
          <a href="/loyalty" className="mt-4 inline-flex items-center text-xs font-semibold text-emerald-400 hover:text-emerald-300">
            Spend your points →
          </a>
        </div>
      </div>

      <RecentSubmissions subs={subs}/>

      {bizOpen && <BlackoutSubmitBusinessModal onClose={() => setBizOpen(false)} onSuccess={onAnySuccess}/>}
      {evOpen && <BlackoutSubmitEventModal onClose={() => setEvOpen(false)} onSuccess={onAnySuccess}/>}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, suffix, tint = 'emerald', testId }) {
  const colors = {
    emerald: 'from-emerald-500/15 to-transparent border-emerald-500/30 text-emerald-300',
    fuchsia: 'from-fuchsia-500/15 to-transparent border-fuchsia-500/30 text-fuchsia-300',
    amber: 'from-amber-500/15 to-transparent border-amber-500/30 text-amber-300',
    sky: 'from-sky-500/15 to-transparent border-sky-500/30 text-sky-300',
  }[tint];
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${colors} bg-neutral-900/60 p-4`} data-testid={testId}>
      <Icon size={16} className="opacity-70"/>
      <div className="mt-2 text-3xl font-black tabular-nums text-white">{value}{suffix && <span className="text-base ml-1 text-neutral-400 font-semibold">{suffix}</span>}</div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1 font-semibold">{label}</div>
    </div>
  );
}

const STATUS_ICON = {
  new: { Icon: CheckCircle2, color: 'text-emerald-400', label: 'New Discovery' },
  duplicate: { Icon: Target, color: 'text-amber-400', label: 'Duplicate' },
  pending_review: { Icon: Clock, color: 'text-sky-400', label: 'Pending Review' },
  approved: { Icon: CheckCircle2, color: 'text-emerald-400', label: 'Approved' },
  rejected: { Icon: AlertCircle, color: 'text-red-400', label: 'Rejected' },
};

function RecentSubmissions({ subs }) {
  if (!subs?.length) return null;
  return (
    <div className="pbb-card p-5" data-testid="blackout-recent-submissions">
      <h3 className="font-bold text-sm uppercase tracking-wider mb-3">Recent Submissions</h3>
      <div className="space-y-2">
        {subs.map((s) => {
          const meta = STATUS_ICON[s.status] || STATUS_ICON.pending_review;
          const { Icon, color, label } = meta;
          return (
            <div key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-black/40 border border-neutral-800" data-testid={`blackout-sub-${s.id}`}>
              <Icon size={16} className={color}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{s?.data?.name || 'Untitled'}</div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500">{s.type} · {label}</div>
              </div>
              {s.pointsAwarded > 0 && (
                <div className="text-sm font-black text-amber-300">+{s.pointsAwarded}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
